/**
 * The words on the Release tab (docs/ux/release.md): the checklist state
 * words, the build state, the failure codes as sentences (never
 * LibreOffice's own message, which could quote the document), the fix
 * links, and the engine's Release sentence.
 */

import { statusLabel, type Status } from '@beam/core/status';
import type { ChecklistKey, ChecklistState, JobState, MatterDetail } from '@/app/types';
import { longDate } from '@/lib/format';

export const STATE_WORD: Record<ChecklistState, string> = { met: 'Met', failed: 'To fix', not_decided: 'Not decided' };
export const BUILD_WORD: Record<JobState, string> = { queued: 'Queued.', running: 'Converting the PDF.', succeeded: 'Built.', failed: 'Failed.' };

/** What a pack holds (ADR-0001 item 3, D-0053; the statutory pack D-0055). */
export const PACK_SENTENCE = 'The pack is the report as a Word document in the house layout, the PDF printed from it, and the HTML preview.';
export const STATUTORY_PACK_SENTENCE = 'The pack is the report as a Word document and its PDF, then the filled statutory form or forms (APIA1, or APIA6 when the condition has not stabilised, with APIA7 for a psychiatric injury) as Word and PDF, and the HTML preview.';
export const NOT_DECIDED_SENTENCE = 'Does not block release in this proof of concept.';

export function failureSentence(code: string | undefined): string {
  switch (code) {
    case 'PDF_CONVERTER_UNAVAILABLE': return 'This host has no PDF converter; the container image carries LibreOffice, the laptop does not.';
    case 'PDF_CONVERSION_FAILED': return 'LibreOffice did not produce a file.';
    case 'LIBREOFFICE_TIMEOUT': return 'The conversion took longer than two minutes and was stopped.';
    case 'CHECKLIST_INCOMPLETE': return 'The checklist changed before the build ran.';
    case 'WORKSPACE_MISSING': return 'The report has no workspace document to print.';
    default: return `The build failed (${code ?? 'no code'}).`;
  }
}

export type FixLink = { href: string; label: string; name: string } | null;

/** Where a failed item is fixed (docs/ux/release.md section 5). */
export function fixLinkFor(key: ChecklistKey, state: ChecklistState, matterRef: string, doctorSlug: string, doctorName: string): FixLink {
  if (state === 'met') return null;
  const ref = encodeURIComponent(matterRef);
  if (key === 'unanswered' || key === 'forbidden' || key === 'dates') return { href: `/matters/${ref}/workspace`, label: 'Open the workspace', name: `Open the workspace to fix ${key === 'unanswered' ? 'the empty box' : key === 'forbidden' ? 'the forbidden characters' : 'the dates'}` };
  if (key === 'signature' || key === 'readiness') return { href: `/registry/doctors/${encodeURIComponent(doctorSlug)}`, label: 'Open the registry', name: `Open the registry record for ${doctorName}` };
  return null;
}

export function summaryLine(items: Array<{ state: ChecklistState }>): string {
  const met = items.filter((i) => i.state === 'met').length;
  const failed = items.filter((i) => i.state === 'failed').length;
  const notDecided = items.filter((i) => i.state === 'not_decided').length;
  return `${met} met, ${failed} to fix, ${notDecided} not decided.`;
}

/** Why Build cannot run now, or null when it can. */
export function buildBlockedReason(view: { canBuild: boolean; converter: 'libreoffice' | 'none'; build: { state: JobState } | null; checklist: { failed: number } }, status: Status, offline: boolean): string | null {
  if (offline) return 'Paused while offline.';
  const running = view.build !== null && (view.build.state === 'queued' || view.build.state === 'running');
  if (running) return null;
  if (!view.checklist || view.checklist.failed > 0) {
    const turn = status === 7 ? 'The report is with QA. ' : status === 6 || status === 8 ? 'The report is with the doctor. ' : '';
    return `${turn}The checklist has ${view.checklist.failed} ${view.checklist.failed === 1 ? 'item' : 'items'} to fix.`;
  }
  if (view.converter === 'none') return failureSentence('PDF_CONVERTER_UNAVAILABLE');
  if (!view.canBuild) return 'A release pack is already being built.';
  return null;
}

/** The engine's answer for Release, repeated as one sentence with the order to press things in. */
export function releaseSentence(detail: MatterDetail): string {
  if (detail.matter.status >= 10) {
    const at = detail.matter.keyDates.released;
    return at ? `Released on ${longDate(at)}.` : `Released. The case is at ${detail.matter.status} ${statusLabel(detail.matter.status)}.`;
  }
  const release = detail.options.find((o) => o.kind === 'release');
  if (!release) return '';
  if (release.allowed) return `Release is allowed: the report moves to ${release.to} ${statusLabel(release.to as Status)}. Build the pack first so the released version exists, then Release from the header.`;
  const reason = release.reason ?? 'the engine refused';
  let hint = '';
  if (/plaintiff/.test(reason)) hint = ' Raise invoice from the header, then Release.';
  else if (/checklist/.test(reason)) hint = ' Fix the items above, then Release from the header.';
  return `Release is refused: ${reason}.${hint}`;
}

export function formatBytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
